import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  convertToParamMap,
  NavigationEnd,
  ParamMap,
  Router,
} from '@angular/router';
import { mapResources } from '@thiememeulenhoff/ng-api-gateway';
import { paramMapWithName } from '@thiememeulenhoff/ng-sdk';
import { UserRecentStore } from '@thiememeulenhoff/ng-store';
import { Arrangement, ArrangementResource } from '@thiememeulenhoff/types';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { ManagementContentService } from './management-content.service';
import { URI_PARAMS } from '../_constants/uri-params';

@Service()
export class ArrangementService {
  #lastArrangementCode?: string;

  readonly #contentService = inject(ManagementContentService);

  readonly #router = inject(Router);

  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #userRecentStore = inject(UserRecentStore);

  readonly #paramsSubject = new BehaviorSubject<ParamMap>(
    convertToParamMap({}),
  );

  readonly arrangementCodeOrNull$ = this.#paramsSubject.pipe(
    map((params) => {
      return params.get(URI_PARAMS.arrangementCode) || null;
    }),
    filter((arrangementCode) => arrangementCode !== undefined),
    distinctUntilChanged(),
  );

  readonly arrangementCode$: Observable<string> =
    this.arrangementCodeOrNull$.pipe(
      filter((arrangementCode) => arrangementCode !== null),
      distinctUntilChanged(),
    );

  readonly arrangement$ = this.arrangementCode$.pipe(
    switchMap((code) => this.#contentService.arrangement$(code)),
    filter((arrangement): arrangement is Arrangement => !!arrangement),
    tap((arrangement) => {
      if (this.#lastArrangementCode === arrangement.code) {
        return;
      }
      this.#lastArrangementCode = arrangement.code;

      if ((arrangement.deeplink ?? '').length > 0) {
        return;
      }

      this.#userRecentStore.setRecentArrangement(
        arrangement.methodObject.slug,
        arrangement.code,
      );
    }),
    distinctUntilChanged((a, b) => a.code === b.code),
  );

  readonly arrangementResource$ = this.arrangement$.pipe(
    switchMap((arrangement) => {
      return this.#contentService.arrangementResourceByCode$(arrangement.code);
    }),
    map((resource) => (resource ? [resource] : [])),
    mapResources(),
  );

  readonly bookResource$ = this.arrangementResource$.pipe(
    map(({ book }) => book),
    filter((book) => !!book),
    map((book) => book as ArrangementResource),
  );

  fetchArrangementWithSources(code: string) {
    return combineLatest([
      this.#contentService.fetchArrangement(code),
      this.#contentService.fetchArrangementResourceWithSources(code),
    ]);
  }

  get title$() {
    return this.arrangement$.pipe(map((arrangement) => arrangement?.title));
  }

  get methodSlug$() {
    return this.arrangement$.pipe(
      map((arrangement) => arrangement?.methodObject?.slug),
    );
  }

  get chapters$() {
    return this.bookResource$.pipe(
      map((resources): Array<ArrangementResource> => {
        return (
          resources.children?.filter((resource) =>
            ['module', 'chapter'].includes(resource.digitalResourceType),
          ) ?? []
        );
      }),
    );
  }

  get isModuleStream$(): Observable<boolean> {
    return this.arrangementResource$.pipe(
      map(({ book }) => !!book?.streamMetadata?.isModuleStream),
    );
  }

  #updateFromParamMap() {
    const paramMap = paramMapWithName(
      URI_PARAMS.arrangementCode,
      this.#activatedRoute.pathFromRoot,
    );

    if (paramMap && paramMap.get(URI_PARAMS.arrangementCode)) {
      this.#paramsSubject.next(paramMap);
      return;
    }

    this.#paramsSubject.next(convertToParamMap({}));
  }

  constructor() {
    this.#updateFromParamMap();

    this.#router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => this.#updateFromParamMap());

    this.arrangementCode$
      .pipe(
        switchMap((code) => this.#contentService.arrangement$(code)),
        filter((arrangement) => !!arrangement),
        distinctUntilChanged((a, b) => a.code === b.code),
      )
      .subscribe((arrangement) => {
        if ((arrangement.deeplink ?? '').length > 0) {
          // arrangements with a deeplink are not marked as recent...
          return;
        }

        this.#userRecentStore.setRecentArrangement(
          arrangement.methodObject.slug,
          arrangement.code,
        );
      });
  }
}
